import classNames from 'classnames';
import _ from 'lodash';
import React, { useState } from 'react';

import { Button } from 'react-bootstrap';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import TagSelect from 'components/TagSelect';
import Tooltip from 'components/Tooltip';
import { DocumentType, DocumentSubtype, isVeevaMedcomms, EntityType } from 'const';
import { AUDIENCE_TYPE, CHANNEL_TYPE, CONTENT_TYPE } from 'const/MedComms/Tags';
import { ProjectMetadataTags } from 'models';
import { TagEntitiesMap, TagEntity } from 'models/entities/Tag';
import { intlGet } from 'utils/intlGet';
import { LayoutDetailsProps } from './models';
import styles from './styles.module.scss';

const LAYOUT_DETAILS_TAGS = 'LayoutDetails.Tags';
const LAYOUT_DETAILS_TAGS_BUTTONS = 'LayoutDetails.Tags.Buttons';

const LayoutDetails: React.FunctionComponent<LayoutDetailsProps> = ({
  options,
  hideModal,
  modalWindowId,
  documentTypes,
  updateReusableLayoutTags,
}) => {
  const onClose = (): void => { hideModal(modalWindowId); };
  const document = options.get('document');
  const metadata = options.get('metadata');
  const classification = document.get('classification');
  const name = document.get('name');
  const number = document.get('number');
  const status = document.get('status');
  const product = document.get('product');
  const thumbnailUrl = document.get('_thumbnailUrl');
  const documentId = document.get('documentId');
  const entityType = document.get('entityType') as EntityType;
  const image = React.createRef<HTMLImageElement>();
  const [fullSized, setFullSized] = useState(false);
  const toggleFullSizedState = (): void => setFullSized(prev => !prev);
  const productName = _.first(product.toJS());

  const [audienceType, setAudienceType] = React.useState<string[]>([]);
  const [channelType, setChannelType] = React.useState<string[]>([]);
  const [contentType, setContentType] = React.useState<string[]>([]);

  const [isEditMode, setIsEditMode] = React.useState(false);

  const updateTagsState = (tags: ProjectMetadataTags): void => {
    setAudienceType(tags.audienceType || []);
    setChannelType(tags.channelType || []);
    setContentType(tags.contentType || []);
  };

  // Load existing tags when component mounts
  React.useEffect(() => {
    if (metadata) {
      updateTagsState(metadata.toJS() as ProjectMetadataTags);
    }
  }, [metadata]);

  const saveButtonRef = React.createRef<HTMLDivElement>();

  const [isSaveButtonTooltipShown, setIsSaveButtonTooltipShown] = React.useState(false);

  const showSaveButtonTooltip = (): void => setIsSaveButtonTooltipShown(true);
  const hideSaveButtonTooltip = (): void => setIsSaveButtonTooltipShown(false);

  const handleCancel = (): void => {
    updateTagsState(metadata.toJS() as ProjectMetadataTags);
    setIsEditMode(false);
  };

  const handleEdit = (): void => {
    setIsEditMode(true);
  };

  const handleSave = (): void => {
    const tags = {
      audienceType,
      channelType,
      contentType,
    };

    // Dispatch the action to trigger the saga
    updateReusableLayoutTags(modalWindowId, documentId, tags);
    setIsEditMode(false);
  };

  const renderTags = (): JSX.Element => {
    const {
      audienceType: audienceTypeTags,
      channelType: channelTypeTags,
      contentType: contentTypeTags,
    } = metadata.toJS() as ProjectMetadataTags;

    return (
      <div className={styles.tagsSectionViewContainer}>
        <span className={styles.tagLabel}>{intlGet(LAYOUT_DETAILS_TAGS, 'Audience Type')}</span>: {audienceTypeTags.join(', ')};
        <span className={styles.tagLabel}> {intlGet(LAYOUT_DETAILS_TAGS, 'Channel Type')}</span>: {channelTypeTags.join(', ')};
        <span className={styles.tagLabel}> {intlGet(LAYOUT_DETAILS_TAGS, 'Content Type')}</span>: {contentTypeTags.join(', ')};
      </div>
    );
  };

  const documentType = documentTypes.get(DocumentType.COMPONENT);
  const documentSubtypesMap = documentType?.get('subtypes');
  const documentSubtypeMap = documentSubtypesMap?.get(DocumentSubtype.CONTENT_UNIT);

  const audienceTypeData = (documentSubtypeMap?.getIn(['properties', AUDIENCE_TYPE]) as TagEntitiesMap)?.toJS() as TagEntity;
  const channelTypeData = (documentSubtypeMap?.getIn(['properties', CHANNEL_TYPE]) as TagEntitiesMap)?.toJS() as TagEntity;
  const contentTypeData = (documentSubtypeMap?.getIn(['properties', CONTENT_TYPE]) as TagEntitiesMap)?.toJS() as TagEntity;

  const hasMissingTags = !audienceType.length || !channelType.length || !contentType.length;
  const isSaveButtonDisabled = isVeevaMedcomms() && hasMissingTags;

  const showTagsSection = isVeevaMedcomms() && entityType !== EntityType.GROUP_LAYOUT;

  return (
    <div className={styles.LayoutDetails}>
      <header>
        <div className={styles.info}>
          <div className={styles.title}>{name}</div>
        </div>
        <Icon className={styles.closeIcon} onClick={onClose} type={IconType.CLOSE} size="sm-md" color="primary" />
      </header>
      <section>
        <div className={styles.metadata}>
          {status && <span className={styles.status}>{status.toUpperCase()}</span>}
          <span className={styles.number}>{number}</span>
          {productName && <span className={styles.valueWithSeparator}>{productName}</span>}
          {classification && <span className={styles.valueWithSeparator}>{classification}</span>}
        </div>
        {showTagsSection && <div className={styles.tagsSection}>
          <div className={styles.tagsSectionRow}>
            <h4 className={styles.tagsSectionRowHeader}>{intlGet(LAYOUT_DETAILS_TAGS, 'Header')}</h4>
          </div>
          {!isEditMode &&
          <div className={styles.tagsSectionView}>
            {renderTags()}
            <div>
              <Button
                onClick={handleEdit}
                variant="primary"
              >
                {intlGet(LAYOUT_DETAILS_TAGS_BUTTONS, 'Edit')}
              </Button>
            </div>
          </div>}
          {isEditMode &&
          <div className={styles.tagsSectionEdit}>
            <div className={styles.tagsSectionRow}>
              <div className={styles.tagsSectionRowTagName}>
                <span>{intlGet(LAYOUT_DETAILS_TAGS, 'Audience Type')}</span>
              </div>
              <div className={styles.tagsSectionRowTagValue}>
                <TagSelect
                  options={audienceTypeData?.entryLabels ?? []}
                  value={audienceType}
                  onChange={setAudienceType}
                  singleSelect={!audienceTypeData?.repeating}
                />
              </div>
            </div>
            <div className={styles.tagsSectionRow}>
              <div className={styles.tagsSectionRowTagName}>
                <span>{intlGet(LAYOUT_DETAILS_TAGS, 'Channel Type')}</span>
              </div>
              <div className={styles.tagsSectionRowTagValue}>
                <TagSelect
                  options={channelTypeData?.entryLabels ?? []}
                  value={channelType}
                  onChange={setChannelType}
                  singleSelect={!channelTypeData?.repeating}
                />
              </div>
            </div>
            <div className={styles.tagsSectionRow}>
              <div className={styles.tagsSectionRowTagName}>
                <span>{intlGet(LAYOUT_DETAILS_TAGS, 'Content Type')}</span>
              </div>
              <div className={styles.tagsSectionRowTagValue}>
                <TagSelect
                  options={contentTypeData?.entryLabels ?? []}
                  value={contentType}
                  onChange={setContentType}
                  singleSelect={!contentTypeData?.repeating}
                />
              </div>
            </div>
            <div className={styles.tagsSectionActions}>
              <Button onClick={handleCancel} variant="secondary">{intlGet(LAYOUT_DETAILS_TAGS_BUTTONS, 'Cancel')}</Button>
              <span
                ref={saveButtonRef}
                onMouseEnter={showSaveButtonTooltip}
                onMouseLeave={hideSaveButtonTooltip}>
                <Button
                  onClick={handleSave}
                  variant="primary"
                  disabled={isSaveButtonDisabled}
                >
                  {intlGet(LAYOUT_DETAILS_TAGS_BUTTONS, 'Save')}
                </Button>
              </span>
              {isVeevaMedcomms() && <Tooltip
                className={styles.tooltip}
                parent={saveButtonRef}
                show={hasMissingTags && isSaveButtonTooltipShown}
                text={intlGet('LayoutDetails.Tags.Buttons.Save', 'Tooltip')} />}
            </div>
          </div>}
        </div>}
        <div className={showTagsSection ? classNames(styles.preview, styles.withTags) : styles.preview}>
          {
            thumbnailUrl
              ?
              <img
                ref={image}
                className={classNames(styles.image, { [styles.fullSize]: fullSized })}
                src={thumbnailUrl}
                onClick={toggleFullSizedState}
              />
              :
              <div className={styles.icon}>
                <Icon type={IconType.IMAGE} size="xxl" color="grey" />
                <span className={styles.iconText}>{intlGet('ProjectPanel.ImageNotAvailable', 'Preview')}</span>
              </div>
          }
        </div>
      </section>
    </div>
  );
};

export default LayoutDetails;
